// src/components/RecipeCard.jsx  —  ohapotato v2026.10.36
import React, { useState, memo } from 'react';
import CookMode from './CookMode';
import ChefMode from './ChefMode';
import NutritionPanel from './NutritionPanel';

// Helper: split steps into prep and cook phases
function splitSteps(steps = []) {
  const prep = steps.filter(s => s.phase === 'prep');
  const cook = steps.filter(s => s.phase === 'cook');
  // Graceful fallback: if no phases tagged, treat first ~35% as prep
  if (prep.length === 0 && cook.length === 0 && steps.length > 0) {
    const split = Math.max(1, Math.ceil(steps.length * 0.35));
    return { prep: steps.slice(0, split), cook: steps.slice(split) };
  }
  // If everything is one phase (e.g. all cook), show under cook only
  if (prep.length === 0) return { prep: [], cook: steps };
  if (cook.length === 0) return { prep: steps, cook: [] };
  return { prep, cook };
}

// Chef Mode session limit — shared across all cards via module-level counter
const CHEF_MODE_SESSION_LIMIT = 2;
let chefModeSessionCount = 0;

const RecipeCard = memo(function RecipeCard({ recipe, index, user, onSave, savedIds = [], onAddToGrocery, onRemoveFromGrocery }) {
  const [expanded, setExpanded]   = useState(false);
  const [cookMode, setCookMode]   = useState(false);
  const [chefMode, setChefMode]   = useState(false);
  const [shopFlash, setShopFlash] = useState(null);
  const [addedNames, setAddedNames] = useState(new Set());
  const [chefLimitHit, setChefLimitHit] = useState(false);
  const isSaved = savedIds.includes(recipe.id);

  // XP scales with match quality: 10 base + up to 90 for 100% match
  const xpValue = Math.round(10 + (recipe.matchPercent / 100) * 90);

  const toggleShopItem = (name) => {
    if (addedNames.has(name)) {
      // Already added — remove it
      if (onRemoveFromGrocery) onRemoveFromGrocery(name);
      setAddedNames(prev => { const next = new Set(prev); next.delete(name); return next; });
      setShopFlash(null);
    } else {
      // Not yet added — add it
      if (onAddToGrocery) onAddToGrocery(name);
      setAddedNames(prev => new Set([...prev, name]));
      setShopFlash(name);
      setTimeout(() => setShopFlash(null), 2000);
    }
  };

  const { prep, cook } = splitSteps(recipe.steps);

  return (
    // The entire card is clickable to toggle expand.
    // Buttons inside use e.stopPropagation() to avoid toggling.
    <div
      style={{
        ...styles.card,
        ...(expanded ? styles.cardExpanded : {}),
        animationDelay: `${index * 0.07}s`,
      }}
      onClick={() => setExpanded(e => !e)}
    >
      {/* ── Header ─────────────────────────────────────────── */}
      <div style={styles.top}>
        <div style={styles.emoji}>
          {recipe.image
            ? <img
                src={recipe.image}
                alt={recipe.title}
                style={styles.emojiImg}
                onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
              />
            : null}
          <span style={{ display: recipe.image ? 'none' : 'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%' }}>
            {recipe.emoji}
          </span>
        </div>
        <div style={styles.meta}>
          <h3 style={styles.title}>{recipe.title}</h3>
          <div style={styles.tags}>
            <span style={styles.tag}>⏱ {recipe.totalTime}</span>
            <span style={styles.tag}>{recipe.difficulty}</span>
            <span style={styles.tag}>🍽 {recipe.servings}</span>
            <span style={{ ...styles.tag, ...styles.tagMatch }}>
              ✦ {recipe.matchPercent}% match
            </span>
            <span className="xp-badge">+{xpValue} XP</span>
          </div>
        </div>
        <div style={{ ...styles.chevron, ...(expanded ? styles.chevronUp : {}) }}>▾</div>
      </div>

      {/* ── Match bar ──────────────────────────────────────── */}
      <div style={styles.matchRow}>
        <span style={styles.matchLabel}>Ingredient match</span>
        <div style={styles.matchBar}>
          <div style={{ ...styles.matchFill, width: `${recipe.matchPercent}%` }} />
        </div>
        <span style={styles.matchPct}>{recipe.matchPercent}%</span>
      </div>

      {recipe.matchPercent < 100 && (
        <div style={styles.noExactMatch}>
          <span style={styles.noExactIcon}>⚠</span>
          <span style={styles.noExactText}>
            No exact match — you have {recipe.matchPercent}% of ingredients.
            {' '}Missing items are marked <span style={{ color:'var(--accent-orange)', fontWeight:600 }}>·</span> below.
          </span>
        </div>
      )}

      {recipe.nutrition && (recipe.nutrition.calories > 0 || recipe.nutrition.protein > 0) && (
        <div style={styles.macroStrip}>
          {recipe.nutrition.calories > 0 && (
            <span style={styles.macroChip}>
              🔥 <strong>{recipe.nutrition.calories}</strong> cal
            </span>
          )}
          {recipe.nutrition.protein > 0 && (
            <span style={styles.macroChip}>
              <span style={{color:'var(--accent-green)'}}>●</span> <strong>{recipe.nutrition.protein}g</strong> protein
            </span>
          )}
          {recipe.nutrition.carbs > 0 && (
            <span style={styles.macroChip}>
              <span style={{color:'var(--accent-gold)'}}>●</span> <strong>{recipe.nutrition.carbs}g</strong> carbs
            </span>
          )}
          {recipe.nutrition.fat > 0 && (
            <span style={styles.macroChip}>
              <span style={{color:'var(--accent-orange)'}}>●</span> <strong>{recipe.nutrition.fat}g</strong> fat
            </span>
          )}
        </div>
      )}

      <p style={styles.description}>{recipe.description}</p>

      {/* ── Expanded detail ────────────────────────────────── */}
      {expanded && (
        <div style={styles.expanded} onClick={e => e.stopPropagation()}>
          <div style={styles.divider} />

          {/* Ingredients */}
          <p style={styles.sectionLabel}>Ingredients</p>
          <div style={styles.ingTable}>
            {(recipe.ingredientDetails || []).map((ing, i) => (
              <div key={i} style={{
                ...styles.ingRow,
                background: i % 2 === 0 ? 'var(--bg-pill)' : 'transparent',
              }}>
                <span style={{
                  ...styles.ingStatus,
                  color: ing.have ? 'var(--accent-green)' : 'var(--accent-orange)',
                }}>
                  {ing.have ? '✓' : '·'}
                </span>
                <span style={styles.ingAmount}>{ing.amount}</span>
                <span style={styles.ingName}>{ing.name}</span>
                {!ing.have && (
                  <button
                      style={{ ...styles.ingAddBtn, ...(addedNames.has(ing.name) ? styles.ingAddBtnAdded : {}) }}
                      onClick={e => { e.stopPropagation(); toggleShopItem(ing.name); }}
                      title={addedNames.has(ing.name) ? 'Remove from list' : 'Add to shopping list'}
                    >
                      {addedNames.has(ing.name) ? '✓ added' : '+ list'}
                    </button>
                )}
              </div>
            ))}
          </div>

          {/* ── PREPARATION steps ──────────────────────────── */}
          {prep.length > 0 && (
            <>
              <div style={styles.phaseHeader}>
                <div style={{ ...styles.phaseLine, background: 'var(--accent-orange)' }} />
                <span style={{ ...styles.phaseLabel, color: 'var(--accent-orange)' }}>
                  🔪 Preparation
                </span>
                <div style={{ ...styles.phaseLine, background: 'var(--accent-orange)' }} />
              </div>
              <div style={styles.steps}>
                {prep.map((step, i) => (
                  <StepRow key={i} step={step} num={i + 1} isLast={i === prep.length - 1} />
                ))}
              </div>
            </>
          )}

          {/* ── COOKING steps ──────────────────────────────── */}
          {cook.length > 0 && (
            <>
              <div style={{ ...styles.phaseHeader, marginTop: prep.length > 0 ? '18px' : '18px' }}>
                <div style={{ ...styles.phaseLine, background: 'linear-gradient(90deg, var(--xp-purple), var(--accent-green))' }} />
                <span style={{ ...styles.phaseLabel, color: 'var(--accent-green)' }}>
                  🔥 Cooking
                </span>
                <div style={{ ...styles.phaseLine, background: 'var(--accent-green)' }} />
              </div>
              <div style={styles.steps}>
                {cook.map((step, i) => (
                  <StepRow
                    key={i}
                    step={step}
                    num={prep.length + i + 1}
                    isLast={i === cook.length - 1}
                    phase="cook"
                  />
                ))}
              </div>
            </>
          )}

          {/* Premium features banner — visible only to premium users */}
          {/* Stats */}
          <div style={styles.statsRow}>
            {[
              { icon:'🔪', label:'Prep',       val: recipe.prepTime },
              { icon:'🔥', label:'Cook',       val: recipe.cookTime },
              { icon:'👥', label:'Serves',     val: recipe.servings },
              { icon:'📊', label:'Difficulty', val: recipe.difficulty },
            ].map(({ icon, label, val }, i, arr) => (
              <React.Fragment key={label}>
                <div style={styles.stat}>
                  <span style={styles.statIcon}>{icon}</span>
                  <div>
                    <div style={styles.statLabel}>{label}</div>
                    <div style={styles.statVal}>{val}</div>
                  </div>
                </div>
                {i < arr.length - 1 && <div style={styles.statDivider} />}
              </React.Fragment>
            ))}
          </div>

          {/* Nutrition breakdown (premium feature — shows teaser for free users) */}
          {recipe.nutrition && (
            <div style={{ marginTop: '14px' }}>
              <NutritionPanel
                nutrition={recipe.nutrition}
                servings={recipe.servings}
              />
            </div>
          )}

          {/* Shopping list flash */}
          {shopFlash && (
            <div style={styles.shopFlash}>
              🛒 Added to shopping list
            </div>
          )}

          {/* Action buttons */}
          <div style={styles.actionRow}>
            {user && (
              <button
                style={{ ...styles.actionBtn, ...styles.saveBtn, ...(isSaved ? styles.saveBtnSaved : {}) }}
                onClick={e => { e.stopPropagation(); onSave && onSave(recipe); }}
              >
                {isSaved ? '✓ Saved' : '♡ Save'}
              </button>
            )}
            <button
              style={{
                ...styles.actionBtn, ...styles.chefBtn,
                ...(chefLimitHit ? styles.chefBtnDisabled : {}),
              }}
              onClick={e => {
                e.stopPropagation();
                if (chefModeSessionCount >= CHEF_MODE_SESSION_LIMIT) {
                  setChefLimitHit(true);
                  return;
                }
                chefModeSessionCount++;
                setChefMode(true);
              }}
              title={chefLimitHit ? 'Chef Mode limit reached for this session' : 'Chef Mode — techniques, pairings, pro tips'}
            >
              {chefLimitHit ? '👨‍🍳 Limit reached' : '👨‍🍳 Chef Mode ✦'}
            </button>
            <button
              style={{ ...styles.actionBtn, ...styles.cookBtn }}
              onClick={e => { e.stopPropagation(); setCookMode(true); }}
            >
              🍳 Cook
            </button>
          </div>
        </div>
      )}

      {cookMode && (
        <CookMode recipe={recipe} onClose={() => setCookMode(false)} />
      )}
      {chefMode && (
        <ChefMode recipe={recipe} onClose={() => setChefMode(false)} />
      )}
    </div>
  );
});

export default RecipeCard;

// ── Sub-component: single step row ───────────────────────────────────────────
function StepRow({ step, num, isLast, phase = 'prep' }) {
  const accent = phase === 'cook' ? 'var(--accent-green)' : 'var(--accent-orange)';
  return (
    <div style={styles.step}>
      <div style={styles.stepLeft}>
        <div style={{ ...styles.stepNum, background: accent }}>{num}</div>
        {!isLast && <div style={styles.stepLine} />}
      </div>
      <div style={styles.stepBody}>
        <p style={styles.stepText}>{step.instruction}</p>
        {step.duration && (
          <span style={{ ...styles.stepTime, color: accent,
            background: phase === 'cook' ? 'var(--accent-green-bg)' : 'var(--accent-orange-bg)' }}>
            ⏱ {step.duration}
          </span>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-sm)',
    padding: '20px 22px',
    transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
    animation: 'fadeUp 0.4s ease both',
    cursor: 'pointer',
    userSelect: 'none',
  },
  cardExpanded: {
    boxShadow: 'var(--shadow-md)',
    borderColor: 'var(--border-strong)',
  },
  top: {
    display: 'flex', alignItems: 'flex-start',
    gap: '14px', marginBottom: '10px',
  },
  emoji: {
    flexShrink: 0, marginTop: '1px',
    width: '52px', height: '52px',
    background: 'var(--bg-pill)', borderRadius: 'var(--radius-md)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', fontSize: '28px', lineHeight: '1',
  },
  emojiImg: {
    width: '100%', height: '100%', objectFit: 'cover',
    display: 'block', borderRadius: 'var(--radius-md)',
  },
  meta:  { flex: 1, minWidth: 0 },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '16px', fontWeight: '700', letterSpacing: '-0.2px',
    color: 'var(--text-primary)', marginBottom: '7px', lineHeight: '1.2',
    transition: 'color 0.25s ease',
  },
  tags: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  tag: {
    fontSize: '12px', fontWeight: '500', padding: '3px 8px',
    borderRadius: 'var(--radius-pill)',
    border: '1px solid var(--border-base)',
    background: 'transparent',
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap',
  },
  tagMatch: {
    background: 'var(--accent-green-bg)', color: 'var(--accent-green)',
    borderColor: 'transparent',
  },
  chevron: {
    color: 'var(--text-muted)', fontSize: '14px',
    transition: 'transform 0.2s ease', flexShrink: 0, lineHeight: '1', marginTop: '4px',
  },
  chevronUp: { transform: 'rotate(180deg)' },
  matchRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
  matchLabel: { fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontWeight: '500' },
  matchBar: {
    flex: 1, height: '4px', background: 'var(--border-base)',
    borderRadius: 'var(--radius-pill)', overflow: 'hidden',
  },
  matchFill: {
    height: '100%',
    background: 'linear-gradient(90deg, var(--accent-green), var(--accent-green-light))',
    borderRadius: 'var(--radius-pill)', transition: 'width 0.6s ease',
  },
  noExactMatch: {
    display: 'flex', alignItems: 'flex-start', gap: '7px',
    background: 'rgba(189,80,22,0.06)',
    border: '1px solid rgba(189,80,22,0.25)',
    borderRadius: 'var(--radius-sm)',
    padding: '7px 10px',
    marginBottom: '8px',
  },
  noExactIcon: {
    fontSize: '12px', color: 'var(--accent-orange)', flexShrink: 0, marginTop: '1px',
  },
  noExactText: {
    fontSize: '14px', color: 'var(--text-secondary)',
    fontFamily: 'var(--font-body)', lineHeight: '1.65',
  },
  macroStrip: {
    display: 'flex', flexWrap: 'wrap', gap: '6px',
    margin: '6px 0 4px',
  },
  macroChip: {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '13px', fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    background: 'var(--bg-pill)',
    border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-pill)',
    padding: '2px 8px',
  },
  matchPct: { fontSize: '12px', fontWeight: '700', color: 'var(--accent-green)', whiteSpace: 'nowrap' },
  description: { fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6', transition: 'color 0.3s ease' },

  expanded: { marginTop: '14px' },
  divider: { height: '1px', background: 'var(--border-base)', marginBottom: '16px', transition: 'background 0.3s ease' },
  sectionLabel: {
    fontSize: '12px', fontWeight: '600', letterSpacing: '1px',
    textTransform: 'uppercase', color: 'var(--text-muted)',
    marginBottom: '10px', transition: 'color 0.3s ease',
  },

  // Ingredient table
  ingTable: {
    borderRadius: 'var(--radius-md)', overflow: 'hidden',
    border: '1px solid var(--border-base)', transition: 'border-color 0.3s ease',
    marginBottom: '4px',
  },
  ingRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '8px 12px', transition: 'background 0.3s ease',
  },
  ingStatus: { fontSize: '12px', fontWeight: '800', width: '14px', flexShrink: 0 },
  ingAmount: {
    fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)',
    minWidth: '80px', flexShrink: 0, transition: 'color 0.3s ease',
  },
  ingName: { fontSize: '13px', color: 'var(--text-secondary)', flex: 1, transition: 'color 0.3s ease' },
  ingNeeded: {
    fontSize: '12px', fontWeight: '600', color: 'var(--accent-orange)',
    background: 'var(--accent-orange-bg)', padding: '3px 8px',
    borderRadius: 'var(--radius-pill)', flexShrink: 0,
  },
  ingAddBtn: {
    fontSize: '12px', fontWeight: '600', color: 'var(--accent-orange)',
    background: 'var(--accent-orange-bg)',
    border: '1px solid var(--accent-orange)',
    padding: '2px 8px', borderRadius: 'var(--radius-pill)',
    cursor: 'pointer', flexShrink: 0, fontFamily: 'var(--font-body)',
  },
  ingAddBtnAdded: {
    background: 'var(--accent-green-bg)',
    color: 'var(--accent-green)',
    borderColor: 'var(--accent-green)',
  },
  ingAdded: {
    fontSize: '12px', fontWeight: '600', color: 'var(--accent-green)',
    background: 'var(--accent-green-bg)', padding: '3px 8px',
    borderRadius: 'var(--radius-pill)', flexShrink: 0,
  },
  shopFlash: {
    fontSize: '13px', fontWeight: '500',
    color: 'var(--text-primary)', background: 'var(--bg-pill)',
    border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-md)', padding: '7px 12px',
    textAlign: 'center', animation: 'fadeUp 0.2s ease',
    fontFamily: 'var(--font-body)',
  },

  // Phase section header
  phaseHeader: {
    display: 'flex', alignItems: 'center', gap: '10px',
    margin: '18px 0 12px',
  },
  phaseLine: { flex: 1, height: '1px', opacity: 0.3 },
  phaseLabel: {
    fontSize: '11.5px', fontWeight: '700', letterSpacing: '1px',
    textTransform: 'uppercase', whiteSpace: 'nowrap',
    fontFamily: 'var(--font-body)',
  },

  // Steps
  steps: { display: 'flex', flexDirection: 'column' },
  step: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  stepLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 },
  stepNum: {
    width: '22px', height: '22px', minWidth: '22px',
    color: '#fff', borderRadius: '50%',
    fontSize: '11px', fontWeight: '600',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.3s ease',
  },
  stepLine: {
    width: '1px', flex: 1, minHeight: '16px',
    background: 'var(--border-base)', margin: '4px 0',
    transition: 'background 0.3s ease',
  },
  stepBody: { flex: 1, paddingBottom: '14px' },
  stepText: {
    fontSize: '13px', color: 'var(--text-secondary)',
    lineHeight: '1.55', marginBottom: '4px', transition: 'color 0.3s ease',
  },
  stepTime: {
    display: 'inline-block', fontSize: '12px', fontWeight: '500',
    padding: '2px 8px', borderRadius: 'var(--radius-pill)',
  },

  // Stats row
  statsRow: {
    display: 'flex', gap: '0', marginTop: '16px', paddingTop: '14px',
    borderTop: '1px solid var(--border-base)', flexWrap: 'wrap',
    transition: 'border-color 0.3s ease',
  },
  stat: { display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 auto', padding: '4px 12px 4px 0' },
  statIcon: { fontSize: '18px' },
  statLabel: { fontSize: '12px', color: 'var(--text-muted)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.8px' },
  statVal:   { fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginTop: '1px', transition: 'color 0.3s ease' },
  statDivider: { width: '1px', background: 'var(--border-base)', margin: '4px 12px 4px 0', alignSelf: 'stretch' },

  // Premium banner
  premiumBanner: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'var(--accent-gold-bg)',
    border: '1px solid var(--accent-gold)',
    borderRadius: 'var(--radius-md)', padding: '7px 12px',
    marginTop: '14px',
  },
  premiumBannerPill: {
    fontSize: '11px', fontWeight: '800', letterSpacing: '0.4px',
    color: 'var(--accent-gold)', fontFamily: 'var(--font-body)', flexShrink: 0,
  },
  premiumBannerText: {
    fontSize: '11.5px', color: 'var(--accent-gold)',
    fontFamily: 'var(--font-body)', fontWeight: '500',
  },

  // Action buttons row
  actionRow: { display: 'flex', gap: '8px', marginTop: '14px' },
  actionBtn: {
    flex: 1, padding: '11px',
    border: '1.5px solid', borderRadius: 'var(--radius-md)',
    background: 'transparent', cursor: 'pointer',
    fontFamily: 'var(--font-body)', fontSize: '13.5px', fontWeight: '600',
    transition: 'all var(--transition)',
  },
  saveBtn: { borderColor: 'var(--accent-green)', color: 'var(--accent-green)' },
  saveBtnSaved: { background: 'var(--accent-green-bg)', cursor: 'default' },
  chefBtn:  { borderColor: 'var(--accent-gold)', color: 'var(--accent-gold)' },
  chefBtnDisabled: { borderColor: 'var(--border-base)', color: 'var(--text-muted)', cursor: 'not-allowed', opacity: 0.5 },
  cookBtn:  { borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' },
};
